import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import {
  getInstitutionDetails,
  getInstitutionDetailsDraft,
  INSTITUTION_DETAILS_VERSION,
  type InstitutionDetailsDraft,
  type InstitutionDetailsOnboardingUpdate,
  type InstitutionDetailsPartial,
  type InstitutionDetailsV2OnboardingKey,
  type InstitutionDetailsV2Partial,
  type InstitutionDetailsVersion,
  institutionDetailsFuturesSchema,
  institutionDetailsOnboardingSchema,
  institutionDetailsV2FuturesCompleteSchema,
  institutionDetailsV2OnboardingCompleteSchema,
  institutionDetailsV2OnboardingSchema,
  postInstitutionDetails,
  postInstitutionDetailsDraft,
} from '@/services/api/profile';

const profileInstitutionDetailsQueryKey = (pid: UserPid) => ['profile', 'institution-details', pid];

const profileInstitutionDetailsDraftQueryKey = (pid: UserPid) => ['profile', 'institution-details-draft', pid];

// A record is complete against its own version's schema
const ONBOARDING_COMPLETE_SCHEMA_BY_VERSION = {
  [INSTITUTION_DETAILS_VERSION.V1]: institutionDetailsOnboardingSchema,
  [INSTITUTION_DETAILS_VERSION.V2]: institutionDetailsV2OnboardingCompleteSchema,
};

const FUTURES_COMPLETE_SCHEMA_BY_VERSION = {
  [INSTITUTION_DETAILS_VERSION.V1]: institutionDetailsFuturesSchema,
  [INSTITUTION_DETAILS_VERSION.V2]: institutionDetailsV2FuturesCompleteSchema,
};

const getIsCompleteOnboarding = async (version: InstitutionDetailsVersion, institutionDetails: InstitutionDetailsPartial | InstitutionDetailsV2Partial | null | undefined) => {
  try {
    await ONBOARDING_COMPLETE_SCHEMA_BY_VERSION[version].parseAsync(institutionDetails);
    return true;
  } catch {
    return false;
  }
};

const getIsCompleteFutures = async (version: InstitutionDetailsVersion, institutionDetails: InstitutionDetailsPartial | InstitutionDetailsV2Partial | null | undefined) => {
  try {
    await FUTURES_COMPLETE_SCHEMA_BY_VERSION[version].parseAsync(institutionDetails);
    return true;
  } catch {
    return false;
  }
};

const profileInstitutionDetailsQueryFn = async () => {
  const institutionDetails = await getInstitutionDetails();
  const version = institutionDetails?.version ?? INSTITUTION_DETAILS_VERSION.V1;

  return {
    ...institutionDetails,
    version: institutionDetails?.version,
    answers: institutionDetails?.answers ?? undefined,
    isCompleteOnboarding: await getIsCompleteOnboarding(version, institutionDetails?.answers),
    isCompleteFutures: await getIsCompleteFutures(version, institutionDetails?.answers),
  };
};

type ProfileInstitutionDetailsQueryResult = Awaited<ReturnType<typeof profileInstitutionDetailsQueryFn>>;

const profileInstitutionDetailsQueryOptions = (pid: UserPid) =>
  queryOptions({
    queryFn: profileInstitutionDetailsQueryFn,
    queryKey: profileInstitutionDetailsQueryKey(pid),
  });

const useProfileInstitutionDetails = () => {
  const { pid } = useUserPid();
  return useQuery(profileInstitutionDetailsQueryOptions(pid));
};

// A new application starts on V2; an existing record keeps its own, so there is no version until the record has loaded
const useProfileInstitutionDetailsVersion = () => {
  const { data, ...rest } = useProfileInstitutionDetails();

  return {
    ...rest,
    data: data ? (data.version ?? INSTITUTION_DETAILS_VERSION.V2) : undefined,
  };
};

const useProfileInstitutionDetailsMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();
  const { data: version } = useProfileInstitutionDetailsVersion();

  return useMutation({
    mutationFn: (body: InstitutionDetailsPartial | InstitutionDetailsOnboardingUpdate | InstitutionDetailsV2Partial) => {
      if (!version) throw new Error('Unable to save institution details before the record has loaded');

      return postInstitutionDetails(version, body);
    },
    onSuccess: async (data) => {
      const queryKey = profileInstitutionDetailsQueryKey(pid);
      const isCompleteOnboarding = await getIsCompleteOnboarding(data.version, data.answers);
      const isCompleteFutures = await getIsCompleteFutures(data.version, data.answers);

      // The draft starts from the live answers and locks them
      await queryClient.invalidateQueries({ queryKey: profileInstitutionDetailsDraftQueryKey(pid) });

      if (!isCompleteFutures) {
        queryClient.setQueryData<Partial<ProfileInstitutionDetailsQueryResult>>(queryKey, {
          version: data.version,
          answers: data.answers,
          isCompleteOnboarding,
          isCompleteFutures,
        });
        return;
      }

      await queryClient.invalidateQueries({ queryKey });
    },
  });
};

const useProfileInstitutionDetailsAnswers = () => {
  const { data, ...rest } = useProfileInstitutionDetails();

  return {
    ...rest,
    data: data?.answers,
    isCompleteOnboarding: data?.isCompleteOnboarding,
    isCompleteFutures: data?.isCompleteFutures,
  };
};

const useProfileInstitutionDetailsV2Answers = () => {
  const { data, ...rest } = useProfileInstitutionDetails();

  return {
    ...rest,
    data: data?.version === INSTITUTION_DETAILS_VERSION.V2 ? (data.answers as InstitutionDetailsV2Partial | undefined) : undefined,
  };
};

const useProfileInstitutionDetailsMeta = () => {
  const { data, ...rest } = useProfileInstitutionDetails();

  return {
    ...rest,
    data: {
      clientCategorisation: data && 'categorisation' in data ? data.categorisation : null,
      isRegulated: data && 'regulated' in data ? data.regulated : null,
    },
  };
};

// A section is fully locked once the draft holds it and its locked answer is already a whole answer for the draft's version
const getFullyLockedKeys = async ({ answers, locked }: InstitutionDetailsDraft) => {
  const lockedSections: Partial<Record<InstitutionDetailsV2OnboardingKey, unknown>> = locked;
  const keys = (Object.keys(answers) as InstitutionDetailsV2OnboardingKey[]).filter((key) => lockedSections[key] !== undefined);
  const results = await Promise.all(keys.map((key) => institutionDetailsV2OnboardingSchema.shape[key].safeParseAsync(lockedSections[key])));

  return keys.filter((_, index) => results[index]?.success);
};

const getProfileInstitutionDetailsDraftQueryResult = async (draft: InstitutionDetailsDraft) => ({
  ...draft,
  isCompleteOnboarding: await getIsCompleteOnboarding(draft.version, draft.answers),
  isCompleteFutures: await getIsCompleteFutures(draft.version, draft.answers),
  fullyLockedKeys: await getFullyLockedKeys(draft),
});

const profileInstitutionDetailsDraftQueryFn = async () => {
  const draft = await getInstitutionDetailsDraft();

  if (!draft) return null;

  return getProfileInstitutionDetailsDraftQueryResult(draft);
};

type ProfileInstitutionDetailsDraftQueryResult = Awaited<ReturnType<typeof profileInstitutionDetailsDraftQueryFn>>;

const profileInstitutionDetailsDraftQueryOptions = (pid: UserPid) =>
  queryOptions({
    queryFn: profileInstitutionDetailsDraftQueryFn,
    queryKey: profileInstitutionDetailsDraftQueryKey(pid),
  });

// No draft when the record has nothing to upgrade
const useProfileInstitutionDetailsDraft = (enabled = true) => {
  const { pid } = useUserPid();

  return useQuery({
    ...profileInstitutionDetailsDraftQueryOptions(pid),
    enabled,
  });
};

const useProfileInstitutionDetailsDraftMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: (body: InstitutionDetailsPartial | InstitutionDetailsOnboardingUpdate | InstitutionDetailsV2Partial) => {
      const draft = queryClient.getQueryData<ProfileInstitutionDetailsDraftQueryResult>(profileInstitutionDetailsDraftQueryKey(pid));

      if (!draft) throw new Error('Unable to save the institution details draft before it has loaded');

      return postInstitutionDetailsDraft(draft.version, body);
    },
    // A save fails once the upgrade has finished elsewhere, which replaced the live record and removed the draft
    onError: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: profileInstitutionDetailsDraftQueryKey(pid) }),
        queryClient.invalidateQueries({ queryKey: profileInstitutionDetailsQueryKey(pid) }),
      ]);
    },
    onSuccess: async (data) => {
      const draftQueryKey = profileInstitutionDetailsDraftQueryKey(pid);

      // The update flow is left as soon as the draft is gone, and where to next is worked out from the live record
      if ('isComplete' in data) {
        await queryClient.invalidateQueries({ queryKey: profileInstitutionDetailsQueryKey(pid) });
        queryClient.setQueryData<ProfileInstitutionDetailsDraftQueryResult>(draftQueryKey, null);

        return;
      }

      queryClient.setQueryData<ProfileInstitutionDetailsDraftQueryResult>(draftQueryKey, await getProfileInstitutionDetailsDraftQueryResult(data));
    },
  });
};

export {
  useProfileInstitutionDetails,
  useProfileInstitutionDetailsAnswers,
  useProfileInstitutionDetailsDraft,
  useProfileInstitutionDetailsDraftMutation,
  useProfileInstitutionDetailsMeta,
  useProfileInstitutionDetailsMutation,
  useProfileInstitutionDetailsV2Answers,
  useProfileInstitutionDetailsVersion,
};
